/**
 * Parse visa form definition into renderable sections.
 * formdata shape: { "Section Name": [{ label_id, label_name, field_type, ... }] }
 */

const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;
const SKIP_NOTE_KEYS = new Set([
  'id',
  '_id',
  '__v',
  'created_at',
  'updated_at',
  'createdAt',
  'updatedAt',
]);

const isObjectIdLike = (value) =>
  typeof value === 'string' && OBJECT_ID_RE.test(value.trim());

const toDisplayText = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const text = String(value);
    return isObjectIdLike(text) ? '' : text;
  }
  if (Array.isArray(value)) {
    return value.map(toDisplayText).filter(Boolean).join('\n');
  }
  if (typeof value === 'object') {
    if (value.all_note) return toDisplayText(value.all_note);
    if (value.note) return toDisplayText(value.note);
    if (value.text) return toDisplayText(value.text);
    if (value.message) return toDisplayText(value.message);
    if (value.description) return toDisplayText(value.description);
    if (value.content) return toDisplayText(value.content);
    if (value.notes) {
      const noteText = toDisplayText(value.notes);
      const nameText = value.name ? toDisplayText(value.name) : '';
      if (!noteText) return nameText;
      return nameText ? `${nameText}: ${noteText}` : noteText;
    }
    if (value.name) return toDisplayText(value.name);
    return Object.entries(value)
      .filter(([key]) => !SKIP_NOTE_KEYS.has(key))
      .map(([, entry]) => toDisplayText(entry))
      .filter(Boolean)
      .join('\n');
  }
  return '';
};

export const formatApiDisplayText = toDisplayText;

export const formatVisaNotes = (notes) => {
  const text = toDisplayText(notes).trim();
  if (!text) return null;
  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !isObjectIdLike(line));
  return lines.length ? lines : null;
};

export const parseVisaFormResponse = (formResponse) => {
  if (!formResponse || formResponse.status === false) {
    return { sections: [], countryList: [], meta: formResponse || null };
  }

  const countryList = Array.isArray(formResponse.countrylist) ? formResponse.countrylist : [];
  const rawFormData = formResponse.formdata;
  const sections = [];

  const normalizeField = (field, index, sectionName) => {
    const name = String(
      field.label_id ||
        field.field_name ||
        field.name ||
        field.input_name ||
        field.id ||
        `${sectionName}_${index}`
    );

    const rawType = String(field.field_type || field.type || field.input_type || 'text').toLowerCase();

    const options =
      field.options ||
      field.option ||
      field.values ||
      field.list_value ||
      field.list_values ||
      [];

    let type = 'text';
    if (rawType.includes('textarea') || rawType === 'longtext') {
      type = 'textarea';
    } else if (rawType.includes('date')) {
      type = 'date';
    } else if (rawType.includes('email')) {
      type = 'email';
    } else if (rawType.includes('number')) {
      type = 'number';
    } else if (rawType.includes('radio')) {
      type = 'radio';
    } else if (rawType.includes('checkbox')) {
      type = 'checkbox';
    } else if (
      rawType.includes('select') ||
      rawType.includes('dropdown') ||
      rawType.includes('country')
    ) {
      type = 'select';
    }
    const looksLikeCountry =
      rawType.includes('country') ||
      /country|nationality|citizenship/i.test(field.label_name || field.label || '');

    // Country fields always use Vughy public countries via CountrySelect auto-load.
    if (looksLikeCountry) {
      type = 'country';
    }

    return {
      name,
      label: toDisplayText(field.label_name || field.label || field.title || name) || name,
      type,
      required: Boolean(
        field.required === '1' ||
          field.required === 1 ||
          field.required === true ||
          field.is_required === '1' ||
          field.is_required === 1
      ),
      options: type === 'country' ? [] : options,
      section: sectionName,
    };
  };

  if (Array.isArray(rawFormData)) {
    const fields = rawFormData.map((field, index) =>
      normalizeField(field, index, 'Application Details')
    );
    if (fields.length) {
      sections.push({ title: 'Application Details', fields });
    }
  } else if (rawFormData && typeof rawFormData === 'object') {
    Object.entries(rawFormData).forEach(([sectionTitle, fields]) => {
      if (!Array.isArray(fields) || fields.length === 0) return;
      sections.push({
        title: sectionTitle,
        fields: fields.map((field, index) => normalizeField(field, index, sectionTitle)),
      });
    });
  }

  return {
    sections,
    countryList,
    meta: {
      visaName: formResponse.visaname?.name || '',
      totalCharge: formResponse.total_visa_charge,
      documents: formResponse.documents,
      documentName: formResponse.documentname,
      notes: formatVisaNotes(formResponse.allnotes),
      processVisa: formResponse.process_visa,
    },
  };
};

/**
 * Route form template id (visaid on route item).
 */
export const getRouteVisaTypeId = (visa) =>
  String(visa?.visa_type_id ?? visa?.visa_typeId ?? visa?.visaTypeId ?? '').trim();

export const getRouteFormTemplateId = (visa) =>
  String(visa?.visaid ?? visa?.visaTypeId ?? visa?.visa_type_id ?? '').trim();

export const getRouteGid = (visa) =>
  String(visa?.gid ?? visa?.process_id ?? visa?.id ?? '').trim();

/** @deprecated use getRouteFormTemplateId */
export const resolveVisaFormVisatype = (visa) => getRouteFormTemplateId(visa);

/**
 * Build visa form request params from a selected route.
 */
export const getVisaFormRequestParams = (visa, routeMeta) => ({
  gid: getRouteGid(visa),
  visatype: getRouteFormTemplateId(visa),
  visaid: getRouteVisaTypeId(visa),
  origincountry: String(routeMeta.from_country_id),
  destinationcountry: String(routeMeta.to_country_id),
});

export const flattenFormSections = (sections) =>
  sections.flatMap((section) => section.fields || []);

export const buildFormFieldLabels = (sections) =>
  Object.fromEntries(flattenFormSections(sections).map((field) => [field.name, field.label]));
